import {BasePopup} from "components/popup/BasePopup"
import styles from "./BatchDailyAttendanceUpdates.module.css"
import {BaseButton, BaseInput, BaseLoading} from "components"
import moment from "moment"
import {useModel, useMutation} from "hooks"
import {useEffect, useState} from "react"
import {Checkbox} from "antd"
import cx from "classnames"
import {handleError, toastError, toastInfo, toastSuccess} from "helpers"
import {attendanceService} from "services"

const isEmptyAttendedMinutes = (value: string | number | null | undefined) =>
    value === null || value === "" || value === undefined

const parseAttendedMinutes = (value: string | number | null | undefined): number | null => {
    if (isEmptyAttendedMinutes(value)) {
        return null
    }

    const parsed = Number(value)
    if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
        return null
    }

    return parsed
}

const normalizeAttendedMinutesInput = (value: string | number | null | undefined) => {
    if (isEmptyAttendedMinutes(value)) {
        return ""
    }

    return String(value).replace(/[^\d]/g, "")
}

type Props = {
    isShow: boolean
    isFetching: boolean
    students: any[]
    date: string
    onClose: () => void
    onSuccess: () => void
}

export function BatchDailyAttendanceUpdates(props: Props) {
    const {isShow, onClose, isFetching, date, students: initialStudents, onSuccess} = props
    const model = useModel()
    const dateFormat = model.getUserDateFormat()

    const [students, setStudents] = useState(initialStudents)

    useEffect(() => {
        if (isShow && !isFetching && initialStudents.length <= 0) {
            toastInfo("There are no students to update")
            onClose()
        }
    }, [isShow, isFetching, initialStudents, onClose])

    useEffect(() => {
        setStudents(
            initialStudents.map((x) => ({
                ...x,
                attendedMinutes: null
            }))
        )
    }, [initialStudents])

    const handleFieldChange = (id: string, key: string, value: any) => {
        setStudents((prev) =>
            prev.map((x) => {
                if (x.profileId === id) {
                    if (key === "student") {
                        return {...x, [key]: value, linkedActivities: [], linkedChecklists: []}
                    }
                    if (key === "attendedMinutes") {
                        return {...x, [key]: normalizeAttendedMinutesInput(value)}
                    }
                    return {...x, [key]: value}
                }
                return x
            })
        )
    }

    const hasAtLeastOneAttendedMinutes = () =>
        students.some((student) => parseAttendedMinutes(student.attendedMinutes) !== null)

    const hasInvalidAttendedMinutes = () =>
        students.some(
            (student) =>
                !isEmptyAttendedMinutes(student.attendedMinutes) &&
                parseAttendedMinutes(student.attendedMinutes) === null
        )

    const onSubmit = () => {
        if (!hasAtLeastOneAttendedMinutes()) {
            toastError("Please input at least one student's attended minutes")
            return
        }

        if (hasInvalidAttendedMinutes()) {
            toastError("Please enter valid whole-number attended minutes")
            return
        }

        const payload = students
            .map((item) => ({
                item,
                attendedMinutes: parseAttendedMinutes(item.attendedMinutes)
            }))
            .filter(({attendedMinutes}) => attendedMinutes !== null)
            .map(({item, attendedMinutes}) => ({
                profileId: item.profileId,
                attendedMinutes: attendedMinutes as number,
                isMakeup: item.isMakeup ?? false,
                majorVersionId: item.student?.currentMajorVersionId?.id,
                campusId: item.student?.campuses[0].id
            }))

        batchUpdatesDailyAttendanceMutation.mutate({
            students: payload,
            date: moment(date).local().format("YYYY-MM-DD")
        })
    }

    const batchUpdatesDailyAttendanceMutation = useMutation(attendanceService.batchUpdatesDailyAttendance, {
        onSuccess: () => {
            toastSuccess("Batch daily attendance updates successfully")
            onSuccess()
        },
        onError: (error) => {
            handleError(error)
        }
    })

    return (
        <BasePopup isShow={isShow} onClose={onClose} width="90vw" leftIcon="PERSON_ADD" destroyOnClose keyboard={false}>
            <div className={styles.container}>
                <div className={styles.formTitle}>
                    Batch Daily Attendance Updates on {moment(date).format(dateFormat)}
                </div>
                <div className={styles.tableContainer}>
                    <div className={styles.rowHeader}>
                        <span className={styles.headerTitle}>Student</span>
                        <span className={styles.headerTitle}>Phase</span>
                        <span className={styles.headerTitle}>Shift</span>
                        <span className={styles.headerTitle}>Major</span>
                        <span className={styles.headerTitle}>Major Version</span>
                        <span className={styles.headerTitle}>
                            Attended Minutes<span className={styles.star}>*</span>
                        </span>
                        <span className={cx(styles.headerTitle, styles.headerCenter)}>Makeup</span>
                    </div>
                    <div className={styles.tableBody}>
                        {students.map((item, index) => {
                            return (
                                <div key={index} className={cx(styles.row, {[styles.oddRow]: index % 2 === 1})}>
                                    <div className={styles.col}>{item.studentName}</div>
                                    <div className={styles.col}>{item.phaseNames}</div>
                                    <div className={styles.col}>{item.shiftNames}</div>
                                    <div className={styles.col}>{item.majorHtml}</div>
                                    <div className={styles.col}>{item.majorVersionHtml}</div>
                                    <div className={styles.col}>
                                        <BaseInput
                                            type="number"
                                            min={0}
                                            value={item.attendedMinutes ?? ""}
                                            onChange={(v) => handleFieldChange(item.profileId, "attendedMinutes", v)}
                                            placeholder="Minutes"
                                        />
                                    </div>
                                    <div className={styles.checkbox}>
                                        <Checkbox
                                            checked={item.isMakeup}
                                            onChange={(v) =>
                                                handleFieldChange(item.profileId, "isMakeup", v.target.checked)
                                            }
                                        />
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
                <div className={styles.actionWrap}>
                    <BaseButton
                        title="Cancel"
                        onClick={onClose}
                        variant="secondary"
                        loading={batchUpdatesDailyAttendanceMutation.isLoading}
                    />
                    <BaseButton
                        title="Submit"
                        onClick={onSubmit}
                        disabled={students.length <= 0}
                        loading={batchUpdatesDailyAttendanceMutation.isLoading}
                    />
                </div>
            </div>
            <BaseLoading isShow={isFetching || batchUpdatesDailyAttendanceMutation.isLoading} />
        </BasePopup>
    )
}
